"use client";

import { sumBy } from "es-toolkit";
import type { RefObject } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { VariableSizeList } from "react-window";
import { VariableSizeList as List } from "react-window";
import { useDebounce } from "use-debounce";

import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import { Skeleton } from "@/components/ui/skeleton";
import { useFocusMarketData } from "@/contexts/focus-market-data";
import { useMobile } from "@/hooks/use-mobile";
import useMouse from "@/hooks/use-mouse";
import {
  DESO_APP_NAME,
  DESO_TOKEN_DISPLAY_NAME,
  FOCUS_TOKEN_DISPLAY_NAME,
  LARGE_WIDTH_BREAKPOINT,
} from "@/utils/constants";
import { desoToUSD, focusToUsd, formatDecimalValue } from "@/utils/currency";
import { cn } from "@/utils/lib";
import type { PartialAccount } from "@/utils/types";

import { TokenOrderBookItem } from "./token-orderbook-item";

export type AmmOrderLevel = {
  AmountInQuoteCurrency: number;
  PriceInQuoteCurrency: number;
  TransactorPublicKeyBase58Check?: string;
  OrderId?: string;
};

export const enum OrderSide {
  ASK = "ASK",
  BID = "BID",
}

export const enum OrderDirection {
  topToBottom = "TOP_TO_BOTTOM",
  bottomToTop = "BOTTOM_TO_TOP",
}

export const COLUMN_WIDTH = [43, 20, 17, 20];

export type HoverRow = {
  level: AmmOrderLevel;
  index: number;
} | null;

const TokenOrderbook = ({
  orders,
  coinUsername,
  coinPublicKey,
  side = OrderSide.ASK,
  direction = side === OrderSide.ASK
    ? OrderDirection.bottomToTop
    : OrderDirection.topToBottom,
  tableClassName,
  wrapperClassName,
  showTitles = true,
  defaultTransactor,
  height = 505,
  tooltipWidth = 240,
  outerWrapperClassName,
  tooltipClassName,
}: {
  orders: AmmOrderLevel[] | null;
  coinUsername: string;
  coinPublicKey: string;
  side?: OrderSide;
  direction?: OrderDirection;
  tableClassName?: string;
  wrapperClassName?: string;
  showTitles?: boolean;
  defaultTransactor?: PartialAccount;
  height?: number;
  tooltipWidth?: number;
  outerWrapperClassName?: string;
  tooltipClassName?: string;
}) => {
  const isAsk = side === OrderSide.ASK;
  const [hoveredRow, setHoveredRow] = useState<HoverRow>(null);
  const debouncedHoverRow = useDebounce<HoverRow>(hoveredRow, 50)[0];
  const {
    focusExchangeRate,
    appState,
    loading: loadingMarketData,
  } = useFocusMarketData();
  const { isDevice } = useMobile(LARGE_WIDTH_BREAKPOINT);
  const ref = useRef<VariableSizeList | null>(null);
  const containerRef = useRef<Element>(null) as RefObject<Element>;
  const { elX, elY, elW, elH } = useMouse(containerRef);
  const xIntersecting = elX && elX < elW;
  const yIntersecting = elY > 0 && elY < elH;
  const isIntersecting = xIntersecting && yIntersecting;
  const desoExchangeRate = appState?.USDCentsPerDeSoExchangeRate;
  const isFocus =
    coinPublicKey === process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY;
  const denominatingTokenDisplayName = isFocus
    ? DESO_TOKEN_DISPLAY_NAME
    : FOCUS_TOKEN_DISPLAY_NAME;

  const convertPrice = (price: number) => {
    return isFocus
      ? desoToUSD(price, desoExchangeRate || 0)
      : focusToUsd(price, focusExchangeRate || 0);
  };

  useEffect(() => {
    if (ref.current && direction === OrderDirection.bottomToTop) {
      ref.current.scrollToItem((orders || [])?.length);
    }
  }, [direction, orders]);

  const getTooltipValue = useMemo(() => {
    if (
      !debouncedHoverRow ||
      !orders?.length ||
      !focusExchangeRate ||
      !desoExchangeRate
    ) {
      return;
    }

    const { index } = debouncedHoverRow;
    const partialOrderbook =
      direction === OrderDirection.bottomToTop
        ? orders.slice(index)
        : orders.slice(0, index + 1);

    const totalTokensSold = sumBy(partialOrderbook, (level) => {
      return level.AmountInQuoteCurrency / level.PriceInQuoteCurrency;
    });

    const totalTokensPurchased = sumBy(partialOrderbook, (level) => {
      return level.AmountInQuoteCurrency;
    });

    return (
      <div>
        <div className="text-sm text-muted-foreground">
          <div
            className="
              unforce-radius grid grid-cols-[30%_65%] items-center gap-[5%]
              border-b py-2
            "
          >
            <div className="text-sm font-medium text-muted">Token Price</div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-1">
                <CoinPriceFormatter
                  price={convertPrice(orders[index].PriceInQuoteCurrency)}
                  className={cn(
                    "font-mono text-sm",
                    isAsk ? "text-red-500" : "text-green-500",
                  )}
                />
                <span className="text-xxs text-muted">USD</span>
              </div>
              <div className="flex items-center gap-1">
                <CoinPriceFormatter
                  isUsd={false}
                  price={orders[index].PriceInQuoteCurrency}
                  className="font-mono text-xs text-foreground"
                />
                <span className="text-xxs text-muted">
                  {denominatingTokenDisplayName}
                </span>
              </div>
            </div>
          </div>
          <div
            className="
              unforce-radius grid grid-cols-[30%_65%] items-center gap-[5%]
              border-b py-2
            "
          >
            <div className="text-sm font-medium text-muted">Total</div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-1">
                <CoinPriceFormatter
                  price={convertPrice(totalTokensPurchased)}
                  className={cn(
                    "font-mono text-sm",
                    isAsk ? "text-red-500" : "text-green-500",
                  )}
                />
                <span className="text-xxs text-muted">USD</span>
              </div>
              <div className="flex items-center gap-1">
                <CoinPriceFormatter
                  isUsd={false}
                  price={totalTokensPurchased}
                  className="font-mono text-xs text-foreground"
                />
                <span className="text-xxs text-muted">
                  {denominatingTokenDisplayName}
                </span>
              </div>
            </div>
          </div>
          <div
            className="
              unforce-radius grid grid-cols-[30%_65%] items-center gap-[5%]
              border-b py-2
            "
          >
            <div className="text-sm font-medium text-muted">Size</div>
            <div className="flex flex-col items-end gap-1">
              <span className="font-mono text-sm text-foreground">
                {formatDecimalValue(totalTokensSold)}
              </span>
              <span className="text-xxs text-muted">${coinUsername}</span>
            </div>
          </div>
          <div className="grid grid-cols-[30%_65%] items-center gap-[5%] py-2">
            <div className="text-sm font-medium text-muted">
              Average
              <br />
              Price
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-1">
                <CoinPriceFormatter
                  price={convertPrice(totalTokensPurchased / totalTokensSold)}
                  className={cn(
                    "font-mono text-sm",
                    isAsk ? "text-red-500" : "text-green-500",
                  )}
                />
                <span className="text-xxs text-muted">USD</span>
              </div>
              <div className="flex items-center gap-1">
                <CoinPriceFormatter
                  isUsd={false}
                  price={totalTokensPurchased / totalTokensSold}
                  className="font-mono text-xs text-foreground"
                />
                <span className="text-xxs text-muted">
                  {denominatingTokenDisplayName}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [debouncedHoverRow, orders]);

  if (loadingMarketData || !focusExchangeRate || !desoExchangeRate) {
    return <Skeleton className="h-[250px] w-full" />;
  }

  const HEADING = [
    <>
      {DESO_APP_NAME} <br />
      <span className="text-muted">Username</span>
    </>,
    <>
      Price <br />
      <span className="text-muted">USD/{denominatingTokenDisplayName}</span>
    </>,
    <>
      Size <br />
      <span className="text-muted">{coinUsername}</span>
    </>,
    <>
      Total <br />
      <span className="text-muted">USD/{denominatingTokenDisplayName}</span>
    </>,
  ];

  return (
    <div className={cn("relative", wrapperClassName)}>
      <div
        className={cn(
          `
            pointer-events-none absolute left-0 z-50 w-[240px] -translate-x-full
            -translate-y-1/2 transform rounded-xl border bg-accent px-4
          `,
          (isIntersecting &&
            isAsk &&
            !!debouncedHoverRow &&
            orders?.length &&
            debouncedHoverRow?.index <= orders.length - 1) ||
            (isIntersecting &&
              !isAsk &&
              !!debouncedHoverRow &&
              debouncedHoverRow?.index >= 0)
            ? "block"
            : "hidden",
          tooltipClassName,
        )}
        style={{
          top: isIntersecting ? `${elY}px` : "0",
          left: isDevice ? `${tooltipWidth}px` : "auto",
        }}
      >
        {getTooltipValue}
      </div>
      <div className="flex flex-col overflow-hidden rounded-[4px] border">
        <div
          className={cn(
            `custom-scrollbar-auto relative w-full`,
            outerWrapperClassName,
          )}
          ref={containerRef as RefObject<HTMLDivElement>}
        >
          <div
            className={cn(
              `relative w-full`,
              (orders || []).length === 0 ? "min-w-full!" : "min-w-[650px]",
              tableClassName,
            )}
          >
            {showTitles && (
              <div
                className="
                  unforce-radius sticky top-0 left-0 z-8 flex w-full
                  items-center border-b bg-background
                "
              >
                {HEADING.map((heading, i) => (
                  <div
                    className={cn(
                      `
                        py-2.5 text-start font-sans text-xs font-medium
                        text-muted-foreground
                        first:pl-4
                      `,
                    )}
                    key={i}
                    style={{ width: `${COLUMN_WIDTH[i]}%` }}
                  >
                    {heading}
                  </div>
                ))}
              </div>
            )}
            {orders?.length ? (
              <List
                ref={ref as RefObject<VariableSizeList>}
                width="100%"
                height={height}
                itemCount={orders?.length}
                itemSize={() => 58}
                itemData={{
                  orders,
                  setHoveredRow,
                  defaultTransactor,
                  direction,
                  focusExchangeRate,
                  desoExchangeRate,
                  coinUsername,
                  isAsk,
                  isFocus,
                  hoveredRow: debouncedHoverRow,
                }}
                className="custom-scrollbar-all"
              >
                {TokenOrderBookItem}
              </List>
            ) : (
              <div
                className="
                  cursor-pointer border-b py-2.5 text-center text-sm
                  transition-none
                "
              >
                No {side} orders found.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenOrderbook;
